import React from "react";
import { Box, Typography } from "@mui/material";
import { SectionHeader } from "./SectionHeader";
import { usePermissions } from "@/hooks/usePermissions";
import Cookies from "js-cookie";

interface OpportunityItem {
  oppNo: string | number | null;
  name: string | null;
  tag: string | null;
  value: string | number | null;
  daysAgo: string | number | null;
}

interface OpportunityRow {
  leftTitle: string;
  rightTitle: string;
  left: OpportunityItem;
  right: OpportunityItem;
}

interface LatestOpportunityGridProps {
  title: string;
  data: OpportunityRow[];
  onRefresh?: () => void;
  singleColumn?: boolean;
}

const LatestOpportunityGrid: React.FC<LatestOpportunityGridProps> = ({
  title,
  data,
  onRefresh,
  singleColumn,
}) => {
  const { currentUser, permissionLoading } = usePermissions();

  const currencySymbol =
    Cookies.get("mobile")
      ? JSON.parse(Cookies.get("mobile")!).currencySymbol
      : currentUser?.CompanyDetail?.CurrencySymbol ?? "";

  if (permissionLoading) return null;
  
  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 1,
        border: "1px solid #ddd",
      }}
    >
      <SectionHeader title={title} />

      <Box sx={{ bgcolor: "#fff", p: 2 }}>
        {data.map((row, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              flexDirection: singleColumn ? "column" : "row",
              justifyContent: "space-between",
              borderBottom: i !== data.length - 1 ? "1px solid #eee" : "none",
              pb: 2,
              mb: 2,
              gap: singleColumn ? 2 : 0,
            }}
          >
            {/* LEFT */}
            <Box sx={{ width: singleColumn ? "100%" : "48%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#2e4734",
                    fontSize: 14,
                    textDecoration: "underline",
                  }}
                >
                  {row.leftTitle}
                </Typography>

                <Typography sx={{ color: "#666", fontSize: 12 }}>
                  {row.left?.daysAgo ?? "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Opp No.</Typography>
                  <Typography>{row.left?.oppNo ?? "-"}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Opp Name</Typography>
                  <Typography>{row.left?.name ?? "-"}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Tag</Typography>
                  <Typography>{row.left?.tag ?? "-"}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    Value ({currencySymbol})
                  </Typography>
                  <Typography>{row.left?.value ?? "-"}</Typography>
                </Box>
              </Box>
            </Box>

            {/* RIGHT */}
            <Box sx={{ width: singleColumn ? "100%" : "48%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#2e4734",
                    fontSize: 14,
                    textDecoration: "underline",
                  }}
                >
                  {row.rightTitle}
                </Typography>

                <Typography sx={{ color: "#666", fontSize: 12 }}>
                  {row.right?.daysAgo ?? "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Opp No.</Typography>
                  <Typography>{row.right?.oppNo ?? "-"}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Opp Name</Typography>
                  <Typography>{row.right?.name ?? "-"}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Tag</Typography>
                  <Typography>{row.right?.tag ?? "-"}</Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    Value ({currencySymbol})
                  </Typography>
                  <Typography>{row.right?.value ?? "-"}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>

  );
};

export default LatestOpportunityGrid;
